import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Avatar,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Refresh, Delete, Edit, ToggleOn, ToggleOff, Image, Link as LinkIcon, FilterList, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { fetchItems, fetchAndAddItem, deleteItem, toggleItemActive, updateItem, fetchCategories } from '../../services/itemService';
import { useNavigate } from 'react-router-dom';

/**
 * Extract offer ID from various 1688 URL formats
 * @param {string} url - The URL string
 * @returns {string|null} - The offer ID or null if not found
 */
const extractOfferId = (url) => {
  const trimmedUrl = url.trim().replace(/"/g, '');
  
  // Pattern 1: offerId=123456789 (case-insensitive)
  if (trimmedUrl.toLowerCase().includes('offerid=')) {
    const match = trimmedUrl.match(/offerId=(\d+)/i);
    return match ? match[1] : null;
  }
  
  // Pattern 2: num_iid=123456789
  if (trimmedUrl.includes('num_iid=')) {
    const match = trimmedUrl.match(/num_iid=(\d+)/);
    return match ? match[1] : null;
  }
  
  // Pattern 3: https://detail.1688.com/offer/123456789.html
  if (trimmedUrl.includes('1688.com/offer/')) {
    const match = trimmedUrl.match(/1688\.com\/offer\/(\d+)/);
    return match ? match[1] : null;
  }
  
  // Pattern 4: Mobile URLs - http://detail.m.1688.com/page/index.htm?offerId=123456789
  if (trimmedUrl.includes('detail.m.1688.com') && trimmedUrl.toLowerCase().includes('offerid=')) {
    const match = trimmedUrl.match(/offerId=(\d+)/i);
    return match ? match[1] : null;
  }
  
  return null;
};

const Items = () => {
  const { t } = useTranslation(['items', 'common']);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [clicksFilter, setClicksFilter] = useState('all'); // all, popular, unpopular, never
  const [priorityFilter, setPriorityFilter] = useState('all'); // all, high, normal
  const [categoryFilter, setCategoryFilter] = useState('all'); // all or category id
  
  // Add item dialog
  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({ url: '', item_number: '', category_id: '' });
  const [extractedItemNumber, setExtractedItemNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  
  // Edit dialog
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    title: '',
    price: '',
    image_url: '',
    item_number: '',
    order_by: 999,
    category_id: ''
  });
  
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Custom pagination controls (for page size)
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  // Load categories once for filters and forms
  const loadCategories = async () => {
    try {
      // For dropdowns we need the full list, not just the first paginated page
      const list = await fetchCategories({ fetchAll: true });
      setCategories(Array.isArray(list) ? list : []);
    } catch (error) {
      // Silent fail – items page still works without categories
    }
  };

  // Fetch items
  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await fetchItems({
        page,
        page_size: pageSize,
        search,
        category_id: categoryFilter !== 'all' ? categoryFilter : undefined
      });
      
      let filteredItems = response.results || [];
      
      // Apply client-side filters
      if (statusFilter !== 'all') {
        filteredItems = filteredItems.filter(item => 
          statusFilter === 'active' ? item.is_active : !item.is_active
        );
      }
      
      if (clicksFilter !== 'all') {
        filteredItems = filteredItems.filter(item => {
          const clicks = item.click_count || 0;
          if (clicksFilter === 'popular') return clicks >= 10;
          if (clicksFilter === 'unpopular') return clicks > 0 && clicks < 10;
          if (clicksFilter === 'never') return clicks === 0;
          return true;
        });
      }
      
      if (priorityFilter !== 'all') {
        filteredItems = filteredItems.filter(item => {
          if (priorityFilter === 'high') return item.order_by < 100;
          if (priorityFilter === 'normal') return item.order_by >= 100;
          return true;
        });
      }
      
      setItems(filteredItems);
      setTotalItems(response.count != null ? response.count : filteredItems.length);
    } catch (error) {
      setSnackbar({ open: true, message: t('messages.failedToLoad'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [page, pageSize, search, statusFilter, clicksFilter, priorityFilter, categoryFilter]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadItems();
      } else {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Initial data load
  useEffect(() => {
    loadCategories();
  }, []);

  // Handle URL change and auto-extract item number
  const handleUrlChange = (url) => {
    setAddForm({ ...addForm, url });
    
    if (url.trim()) {
      const extracted = extractOfferId(url);
      if (extracted) {
        setExtractedItemNumber(extracted);
        setAddForm({ ...addForm, url, item_number: extracted });
      } else {
        setExtractedItemNumber('');
      }
    } else {
      setExtractedItemNumber('');
    }
  };

  // Handle add item
  const handleAddItem = async () => {
    // Use extracted item number or manually entered one
    const itemNumberToUse = addForm.item_number || extractedItemNumber;
    
    if (!itemNumberToUse) {
      setSnackbar({ open: true, message: t('messages.provideUrlOrNumber'), severity: 'warning' });
      return;
    }

    setAddLoading(true);
    try {
      // Send item_number and optional category_id to backend
      const payload = {
        item_number: itemNumberToUse
      };
      if (addForm.category_id) {
        payload.category_id = addForm.category_id;
      }

      const response = await fetchAndAddItem(payload);
      if (response.success) {
        setSnackbar({ open: true, message: t('messages.itemAdded'), severity: 'success' });
        setAddDialog(false);
        setAddForm({ url: '', item_number: '', category_id: '' });
        setExtractedItemNumber('');
        loadItems();
      } else {
        setSnackbar({ open: true, message: response.error || t('messages.failedToAdd'), severity: 'error' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || t('messages.failedToAdd');
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setAddLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditForm({
      id: item.id,
      title: item.title,
      price: item.price,
      image_url: item.image_url,
      item_number: item.item_number,
      order_by: item.order_by || 999,
      category_id: item.category?.id || ''
    });
    setEditDialog(true);
  };

  const handleUpdateItem = async () => {
    try {
      // Only send editable fields to backend
      const payload = {
        order_by: editForm.order_by
      };
      // Allow clearing category by sending null
      payload.category_id = editForm.category_id || null;

      await updateItem(editForm.id, payload);
      setSnackbar({ open: true, message: t('messages.itemUpdated'), severity: 'success' });
      setEditDialog(false);
      loadItems();
    } catch (error) {
      setSnackbar({ open: true, message: t('messages.failedToUpdate'), severity: 'error' });
    }
  };

  // Handle delete
  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteItem(deleteDialog.item.id);
      setSnackbar({ open: true, message: t('messages.itemDeleted'), severity: 'success' });
      setDeleteDialog({ open: false, item: null });
      loadItems();
    } catch (error) {
      const isForbidden = error?.response?.status === 403;
      const message = isForbidden ? t('messages.failedToDeletePermission') : t('messages.failedToDelete');
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // Handle toggle active
  const handleToggleActive = async (item) => {
    try {
      await toggleItemActive(item.id);
      setSnackbar({ 
        open: true, 
        message: item.is_active ? t('messages.itemDeactivated') : t('messages.itemActivated'), 
        severity: 'success' 
      });
      loadItems();
    } catch (error) {
      const isForbidden = error?.response?.status === 403;
      const message = isForbidden ? t('messages.failedToTogglePermission') : t('messages.failedToToggle');
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  // Add serial numbers to items for the current page
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: ((page - 1) * rowsPerPage) + idx + 1
    }));

  // Pagination handlers (header-level controls)
  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(String(pageSize));
    } else {
      setPageSize(Number(value));
      setPage(1);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPageSize(val);
      setPage(1);
    }
    setCustomRowsActive(false);
  };

  const columns = [
    { field: 'sn', headerName: t('table.sn'), width: 70, sortable: false, filterable: false, hideable: false },
    { 
      field: 'image_url', 
      headerName: t('table.image'), 
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Avatar 
          src={params.row.image_url} 
          variant="rounded"
          sx={{ width: 50, height: 50 }}
        >
          <Image />
        </Avatar>
      )
    },
    { field: 'item_number', headerName: t('table.itemNumber'), width: 130, sortable: true },
    { 
      field: 'order_by', 
      headerName: t('table.priority'), 
      width: 100,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.row.order_by}
          size="small"
          color={params.row.order_by < 100 ? 'error' : 'default'}
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { 
      field: 'click_count', 
      headerName: t('table.clicks'), 
      width: 100,
      sortable: true,
      type: 'number',
      renderCell: (params) => (
        <Chip
          label={params.row.click_count || 0}
          size="small"
          color={params.row.click_count > 10 ? 'success' : params.row.click_count > 5 ? 'warning' : 'default'}
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { 
      field: 'title', 
      headerName: t('table.title'), 
      flex: 1,
      minWidth: 250,
      sortable: true
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 160,
      sortable: false,
      renderCell: (params) => params.row.category?.name || 'No category'
    },
    { 
      field: 'price', 
      headerName: t('table.price'), 
      width: 120,
      sortable: true,
      type: 'number',
      renderCell: (params) => `¥${parseFloat(params.row.price).toFixed(2)}`
    },
    { 
      field: 'is_active', 
      headerName: t('table.status'), 
      width: 100,
      sortable: true,
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          label={params.row.is_active ? t('table.active') : t('table.inactive')}
          size="small"
          color={params.row.is_active ? 'success' : 'default'}
        />
      )
    },
    { 
      field: 'operator', 
      headerName: t('table.addedBy'), 
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const operator = params.row?.operator;
        return operator?.username || operator?.email || 'N/A';
      }
    },
    { 
      field: 'created_at', 
      headerName: t('table.addedOn'), 
      width: 150,
      sortable: true,
      type: 'date',
      valueGetter: (params) => params ? new Date(params) : null,
      renderCell: (params) => new Date(params.row.created_at).toLocaleDateString()
    },
    { 
      field: 'last_clicked_at', 
      headerName: t('table.lastClicked'), 
      width: 150,
      sortable: true,
      type: 'date',
      valueGetter: (params) => params ? new Date(params) : null,
      renderCell: (params) => params.row.last_clicked_at 
        ? new Date(params.row.last_clicked_at).toLocaleDateString()
        : t('table.never')
    },
    {
      field: 'actions',
      headerName: t('table.actions'),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={params.row.is_active ? t('tooltips.deactivate') : t('tooltips.activate')}>
            <IconButton 
              size="small" 
              onClick={() => handleToggleActive(params.row)}
              color={params.row.is_active ? 'success' : 'default'}
            >
              {params.row.is_active ? <ToggleOn /> : <ToggleOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tooltips.edit')}>
            <IconButton size="small" color="primary" onClick={() => handleEdit(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('tooltips.delete')}>
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C', mb: 2 }}>
          {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadItems}
            disabled={loading}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/admin/items/category')}
          >
            Manage Categories
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialog(true)}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('buttons.addItem')}
          </Button>
        </Box>
      </Box>

      {/* Search & Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {/* Search */}
            <TextField
              fullWidth
              label={t('search.label')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search.placeholder')}
            />
            
            {/* Filters */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FilterList sx={{ color: '#DC143C' }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('filters.title')}
              </Typography>
            </Box>
            
            <Grid container spacing={2} alignItems="center">
              {/* Status Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('filters.status')}</InputLabel>
                  <Select
                    value={statusFilter}
                    label={t('filters.status')}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('filters.allStatus')}</MenuItem>
                    <MenuItem value="active">{t('filters.activeOnly')}</MenuItem>
                    <MenuItem value="inactive">{t('filters.inactiveOnly')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Clicks Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('filters.popularity')}</InputLabel>
                  <Select
                    value={clicksFilter}
                    label={t('filters.popularity')}
                    onChange={(e) => setClicksFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('filters.allItems')}</MenuItem>
                    <MenuItem value="popular">{t('filters.popular')}</MenuItem>
                    <MenuItem value="unpopular">{t('filters.someClicks')}</MenuItem>
                    <MenuItem value="never">{t('filters.neverClicked')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Priority Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>{t('filters.priority')}</InputLabel>
                  <Select
                    value={priorityFilter}
                    label={t('filters.priority')}
                    onChange={(e) => setPriorityFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('filters.allPriorities')}</MenuItem>
                    <MenuItem value="high">{t('filters.highPriority')}</MenuItem>
                    <MenuItem value="normal">{t('filters.normalPriority')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Category Filter */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All categories</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Reset Filters */}
              {(statusFilter !== 'all' || clicksFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all') && (
                <Grid size={{ xs: 12 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setStatusFilter('all');
                      setClicksFilter('all');
                      setPriorityFilter('all');
                      setCategoryFilter('all');
                    }}
                    fullWidth
                  >
                    {t('buttons.resetFilters')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(items, page, pageSize)}
              columns={columns}
              loading={loading}
              pageSize={pageSize}
              pagination
              paginationMode="server"
              rowCount={totalItems}
              page={page - 1}
              disableSelectionOnClick
              hideFooter
              initialState={{
                sorting: {
                  sortModel: [{ field: 'order_by', sort: 'asc' }], // Default sort by priority
                },
              }}
              disableColumnFilter={false}
              disableColumnMenu={false}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
                  visibility: 'visible',
                },
                '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
                  color: 'white',
                },
                '& .MuiDataGrid-sortIcon': {
                  color: 'white',
                },
                '& .MuiDataGrid-menuIcon': {
                  color: 'white',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3'
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page <= 1}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page} of {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(pageSize) ? pageSize : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
            />
          )}
        </Box>
      </Box>

      {/* Add Item Dialog */}
      <Dialog 
        open={addDialog} 
        onClose={() => {
          setAddDialog(false);
          setAddForm({ url: '', item_number: '', category_id: '' });
          setExtractedItemNumber('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('dialogs.addItem.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="info">
              {t('dialogs.addItem.info')}
            </Alert>
            <TextField
              label={t('dialogs.addItem.urlLabel')}
              value={addForm.url}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder={t('dialogs.addItem.urlPlaceholder')}
              fullWidth
              multiline
              rows={2}
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            {extractedItemNumber && (
              <Alert severity="success" sx={{ py: 0.5 }}>
                {t('dialogs.addItem.itemNumberExtracted')} <strong>{extractedItemNumber}</strong>
              </Alert>
            )}
            <Typography align="center" color="text.secondary">{t('dialogs.addItem.or')}</Typography>
            <TextField
              label={t('dialogs.addItem.itemNumberLabel')}
              value={addForm.item_number}
              onChange={(e) => setAddForm({ ...addForm, item_number: e.target.value })}
              placeholder={t('dialogs.addItem.itemNumberPlaceholder')}
              fullWidth
              helperText={t('dialogs.addItem.itemNumberHelper')}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={addForm.category_id || ''}
                label="Category"
                onChange={(e) =>
                  setAddForm({ ...addForm, category_id: e.target.value })
                }
              >
                <MenuItem value="">No category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAddDialog(false);
            setAddForm({ url: '', item_number: '', category_id: '' });
            setExtractedItemNumber('');
          }}>{t('buttons.cancel')}</Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            disabled={addLoading || (!addForm.item_number && !extractedItemNumber)}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {addLoading ? <CircularProgress size={24} /> : t('buttons.fetchAndAdd')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('dialogs.editItem.title')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Alert severity="info">
              {t('dialogs.editItem.info')}
            </Alert>
            
            {/* Read-only item information */}
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">{t('dialogs.editItem.itemNumber')}</Typography>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>{editForm.item_number}</Typography>
              
              <Typography variant="caption" color="text.secondary">{t('dialogs.editItem.titleLabel')}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>{editForm.title}</Typography>
              
              <Typography variant="caption" color="text.secondary">{t('dialogs.editItem.priceLabel')}</Typography>
              <Typography variant="body2">¥{parseFloat(editForm.price || 0).toFixed(2)}</Typography>
            </Box>

            {/* Editable fields */}
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={editForm.category_id || ''}
                label="Category"
                onChange={(e) =>
                  setEditForm({ ...editForm, category_id: e.target.value })
                }
              >
                <MenuItem value="">No category</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('dialogs.editItem.priorityLabel')}
              type="number"
              value={editForm.order_by}
              onChange={(e) => setEditForm({ ...editForm, order_by: parseInt(e.target.value) || 999 })}
              fullWidth
              autoFocus
              helperText={t('dialogs.editItem.priorityHelper')}
              inputProps={{ min: 1, max: 9999 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('buttons.cancel')}</Button>
          <Button
            onClick={handleUpdateItem}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('buttons.updatePriority')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })}>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('dialogs.deleteItem.title')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('dialogs.deleteItem.message')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
            {deleteDialog.item?.title}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>{t('buttons.cancel')}</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Items;

